import { useEffect, useRef, useState } from 'react';
import {
  CloseCircleOutline,
  DocumentDownloadOutline,
  DocumentUploadOutline,
  useToast,
} from '@aslanpay/portal-common-lib';
import { Box, Flex, IconButton, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';

import {
  ACCEPTED_PDF_TYPE,
  DOCUMENT_LIMIT,
  DOCUMENT_NAME_MAX,
  DocumentsFieldsProps,
  MAX_PDF_SIZE_MB,
  SortableDocumentRowProps,
} from './DocumentsFields.types';

function SortableDocumentRow({
  field,
  index,
  onRemove,
  onDownload,
  isDownloading,
}: SortableDocumentRowProps) {
  const {
    register,
    watch,
    trigger,
    formState: { errors, touchedFields },
  } = useFormContext();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const displayName = watch(`documents.${index}.displayName`) ?? '';
  const fileName = watch(`documents.${index}.fileName`) ?? '';
  const fileUploadId = watch(`documents.${index}.fileUploadId`) ?? '';

  const [fetchedName, setFetchedName] = useState('');

  useEffect(() => {
    if (!fileName && fileUploadId) {
      FileProcessingApiHandler.getFileById(fileUploadId)
        .then((data) => setFetchedName(data.fileUpload?.name ?? ''))
        .catch(() => {});
    }
  }, [fileUploadId, fileName]);

  const displayFileName = fileName || fetchedName;

  const docErrors = (
    errors.documents as Record<string, { displayName?: { message?: string } }> | undefined
  )?.[index];
  const docTouched = (
    touchedFields.documents as Record<string, { displayName?: boolean }> | undefined
  )?.[index];

  return (
    <Flex
      ref={setNodeRef}
      align="flex-start"
      gap="12px"
      w="full"
      transform={CSS.Transform.toString(transform)}
      transition={transition}
      opacity={isDragging ? 0.4 : 1}
    >
      <Text
        mt="12px"
        cursor="grab"
        userSelect="none"
        color="brand.darkGray"
        fontSize="16px"
        {...attributes}
        style={{ touchAction: 'none' }}
        {...listeners}
      >
        ⠿
      </Text>

      <Flex flex={1} gap="12px" align="flex-start" minW={0}>
        <Box flex={2} minW={0}>
          <Input
            value={displayFileName}
            isReadOnly
            placeholder="Loading..."
            color="brand.darkGray"
            _readOnly={{ cursor: 'default', bg: 'brand.lightGray2' }}
          />
        </Box>

        <Box flex={1} minW={0}>
          <Box position="relative" w="full">
            <Input
              placeholder="Display name"
              maxLength={DOCUMENT_NAME_MAX}
              isInvalid={!!docErrors?.displayName && !!docTouched?.displayName}
              {...register(`documents.${index}.displayName`, {
                onBlur: () => trigger(`documents.${index}.displayName`),
              })}
            />
            <Text
              position="absolute"
              bottom="5px"
              right="5px"
              textStyle="extraExtraSmallMedium"
              color="brand.darkGray"
            >
              {displayName.length} / {DOCUMENT_NAME_MAX}
            </Text>
          </Box>
          {docErrors?.displayName?.message && docTouched?.displayName && (
            <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
              {docErrors.displayName.message}
            </Text>
          )}
        </Box>
      </Flex>

      <IconButton
        mt="12px"
        variant="link"
        aria-label="Download document"
        icon={
          isDownloading ? (
            <Spinner size="sm" color="brand.purple" />
          ) : (
            <DocumentDownloadOutline boxSize="24px" color="brand.purple" />
          )
        }
        onClick={() => onDownload(index)}
        isDisabled={isDownloading}
      />

      <IconButton
        mt="12px"
        variant="link"
        aria-label="Remove document"
        icon={<CloseCircleOutline boxSize="24px" color="brand.purple" />}
        onClick={() => onRemove(index)}
      />
    </Flex>
  );
}

function DocumentsFields({ companyId }: DocumentsFieldsProps) {
  const { control, getValues } = useFormContext();
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { fields, append, remove, move } = useFieldArray({ control, name: 'documents' });

  const handleDownload = async (index: number) => {
    const fileUploadId = getValues(`documents.${index}.fileUploadId`);
    if (!fileUploadId) return;

    setDownloadingIndex(index);
    try {
      const { url } = await FileProcessingApiHandler.getDownloadPreSignedUrl(fileUploadId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Download failed',
        description: 'Could not retrieve the document. Please try again.',
      });
    } finally {
      setDownloadingIndex(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    move(oldIndex, newIndex);
  };

  const processFile = async (file: File) => {
    if (file.type !== ACCEPTED_PDF_TYPE) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Invalid file type',
        description: 'Only PDF files are accepted.',
      });
      return;
    }

    if (file.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'File too large',
        description: `PDF must be under ${MAX_PDF_SIZE_MB}MB.`,
      });
      return;
    }

    setIsUploading(true);
    try {
      const { id, url } = await FileProcessingApiHandler.getBenefitDocumentUploadPreSignedUrl(
        file.name,
        companyId
      );

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/pdf' },
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload file to S3');

      const { status } = await FileProcessingApiHandler.verifyUpload(id);

      if (status !== 'SUCCEEDED') throw new Error('Upload verification failed');

      append({ fileUploadId: id, displayName: '', fileName: file.name });
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Upload failed',
        description: 'Could not upload the PDF. Please try again.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  return (
    <Stack spacing="5px" mt="5px">
      {fields.length > 0 && (
        <Text textAlign="right" textStyle="smallMedium" color="brand.mutedPurple">
          {fields.length}/{DOCUMENT_LIMIT}
        </Text>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          <Stack spacing="10px">
            {fields.map((field, index) => (
              <SortableDocumentRow
                key={field.id}
                field={field}
                index={index}
                onRemove={remove}
                onDownload={handleDownload}
                isDownloading={downloadingIndex === index}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {fields.length < DOCUMENT_LIMIT && (
        <Box mt="15px">
          {isUploading ? (
            <Flex
              w="full"
              h="100px"
              align="center"
              justify="center"
              border="1.5px dashed"
              borderColor="brand.mediumPurple"
              borderRadius="8px"
              bg="brand.lightGray2"
            >
              <Spinner color="brand.purple" />
            </Flex>
          ) : (
            <Flex
              w="full"
              h="100px"
              align="center"
              justify="center"
              gap="8px"
              bg={isDragOver ? 'brand.lightPurple' : 'brand.lightGray2'}
              border="1.5px dashed"
              borderColor={isDragOver ? 'brand.purple' : 'brand.mediumPurple'}
              borderRadius="8px"
              cursor="default"
              _hover={{ cursor: 'grab' }}
              transition="all 0.15s"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              role="button"
            >
              <DocumentUploadOutline boxSize="20px" color="brand.purple" />
              <Text textStyle="smallMedium" color="brand.purple">
                Upload
              </Text>
              <input ref={inputRef} type="file" accept=".pdf" hidden onChange={handleChange} />
            </Flex>
          )}
        </Box>
      )}
    </Stack>
  );
}

export default DocumentsFields;
